import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth, db } from './firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import './LinkedInTheme.css';

function InviteEmployees() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [emails, setEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      setOrgLoading(true);
      setError(null);
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          setUser(currentUser);
          const idToken = await currentUser.getIdToken();
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const data = userDoc.data();
            const profile = data.profile || {};
            // Robust role check: look in profile.roles AND root roles
            const roles = profile.roles || data.roles || [];
            
            setUserProfile(profile);
            setUserRoles(roles);

            if (roles.includes('admin')) {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/organizations`, {
                headers: { 'Authorization': `Bearer ${idToken}` },
              });
              const resData = await response.json();
              if (!response.ok) throw new Error(resData.error || 'Failed to fetch organizations.');
              
              if (resData.success) {
                setAllOrganizations(resData.organizations);
                if (resData.organizations.length > 0) {
                  const initialOrgId = profile.orgId || resData.organizations[0].id;
                  setSelectedOrgId(initialOrgId);
                  setOrganization(resData.organizations.find(o => o.id === initialOrgId));
                }
              }
            } else if (roles.includes('hr')) {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/my-organization`, {
                headers: { 'Authorization': `Bearer ${idToken}` },
              });
              const resData = await response.json();
              if (!response.ok) throw new Error(resData.error || 'Failed to fetch your organization.');

              if (resData.success) {
                setOrganization(resData.organization);
                setSelectedOrgId(resData.organization.id);
              }
            }
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setOrgLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const emailList = emails.split(/[\s,;]+/).map(email => email.trim()).filter(Boolean);

    if (emailList.length === 0) {
      setError(t('inviteEmployees.errorNoEmails'));
      setLoading(false);
      return;
    }

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('You must be logged in.');
      const idToken = await currentUser.getIdToken();

      const payload = { emails: emailList };
      if (userRoles.includes('admin')) {
        payload.orgId = selectedOrgId;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/invite-employees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invitations.');
      }
      
      if (data.sent.length === 0) {
        setError(t('inviteEmployees.errorNoInvitationsSent'));
      } else {
        let successMessage = t('inviteEmployees.successMessage', { count: data.sent.length });
        if (data.invalid && data.invalid.length > 0) {
          successMessage += ' ' + t('inviteEmployees.invalidMessage', { emails: data.invalid.join(', ') });
        }
        setSuccess(successMessage);
        setEmails('');
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="li-layout">
      <div className="li-container">
        
        {/* Left Sidebar: Profile & Navigation */}
        <aside className="li-sidebar-left">
           <div className="li-card li-profile-card">
              <div className="li-card-background"></div>
              <div className="li-card-content">
                 <img 
                    src={user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.displayName || user?.email || 'User')}&background=random`} 
                    alt="Profile" 
                    className="li-avatar" 
                    onClick={() => navigate('/profile')}
                 />
                 <div className="li-identity">
                    <h3 className="li-name" onClick={() => navigate('/profile')} style={{cursor: 'pointer'}}>
                        {userProfile?.displayName || user?.email?.split('@')[0]}
                    </h3>
                    <p className="li-headline">{userProfile?.headline || t('welcome.subtitle')}</p>
                 </div>
              </div>
           </div>
           
           <div className="li-card">
              <div style={{ padding: '12px' }}>
                 <button className="li-btn-outline-secondary" style={{ width: '100%', textAlign: 'center' }} onClick={() => navigate('/hr-dashboard')}>
                    ← {t('inviteEmployees.backToDashboard')}
                 </button>
              </div>
           </div>
        </aside>

        {/* Main Feed: Invite Form */}
        <main className="li-main-feed">
           <div className="li-card">
               <div className="li-card-header">
                   <h3>{t('inviteEmployees.title')}</h3>
               </div>
               <div className="li-card-body">
                   {error && <div className="li-error-msg" style={{ marginBottom: '16px', textAlign: 'center' }}>{error}</div>}

                   {orgLoading ? (
                     <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>{t('inviteEmployees.loadingOrg')}</div>
                   ) : organization ? (
                     <>
                        <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f3f2ef', borderRadius: '8px' }}>
                          {userRoles.includes('admin') ? (
                            <div className="li-form-stacked">
                              <label htmlFor="organization-select">{t('inviteEmployees.selectOrg')}</label>
                              <select
                                id="organization-select"
                                value={selectedOrgId}
                                onChange={(e) => {
                                  const newOrgId = e.target.value;
                                  setSelectedOrgId(newOrgId);
                                  setOrganization(allOrganizations.find(o => o.id === newOrgId));
                                }}
                                className="li-input"
                                style={{ width: '100%' }}
                              >
                                {allOrganizations.map(org => (
                                  <option key={org.id} value={org.id}>{org.name}</option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px' }}>{t('inviteEmployees.organization')}: {organization.name}</h3>
                          )}
                          
                          <div style={{ fontSize: '13px', color: 'rgba(0,0,0,0.7)' }}>
                            <strong>{t('inviteEmployees.allowedDomains')}:</strong>{' '}
                            <span>{(organization.allowedDomains || []).join(', ')}</span>
                          </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>{t('inviteEmployees.formTitle')}</h3>
                          <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)' }}>{t('inviteEmployees.formSubtitle')}</p>
                        </div>

                        <form onSubmit={handleInvite}>
                          <div className="li-form-stacked">
                            <label htmlFor="emails">{t('inviteEmployees.emailLabel')}</label>
                            <textarea
                              id="emails"
                              value={emails}
                              onChange={(e) => setEmails(e.target.value)}
                              placeholder={t('inviteEmployees.emailPlaceholder')}
                              rows="6"
                              className="li-input"
                              style={{ width: '100%', resize: 'vertical', minHeight: '100px' }}
                            />
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                              <button type="submit" className="li-btn-primary" disabled={loading}>
                                {loading ? t('inviteEmployees.sendingButton') : t('inviteEmployees.sendButton')}
                              </button>
                          </div>
                          
                          {error && <div className="li-error-msg" style={{ marginTop: '16px', textAlign: 'center' }}>{error}</div>}
                          {success && <div className="li-success-msg" style={{ marginTop: '16px', textAlign: 'center' }}>{success}</div>}
                        </form>
                     </>
                   ) : (
                      !error && <div className="li-info-msg" style={{ padding: '20px', textAlign: 'center'}}>{t('inviteEmployees.noOrg')}</div>
                   )}
               </div>
           </div>
        </main>

        {/* Right Sidebar: Info */}
        <aside className="li-sidebar-right">
           <div className="li-card li-promo-card">
               <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                   {t('inviteEmployees.whyInviteTeamTitle')}
               </div>
               <ul style={{ textAlign: 'left', fontSize: '13px', color: 'rgba(0,0,0,0.7)', paddingLeft: '20px', lineHeight: '1.5' }}>
                   <li style={{ marginBottom: '8px' }} dangerouslySetInnerHTML={{ __html: t('inviteEmployees.whyInviteTeamBenefit1') }}></li>
                   <li style={{ marginBottom: '8px' }} dangerouslySetInnerHTML={{ __html: t('inviteEmployees.whyInviteTeamBenefit2') }}></li>
                   <li style={{ marginBottom: '8px' }} dangerouslySetInnerHTML={{ __html: t('inviteEmployees.whyInviteTeamBenefit3') }}></li>
               </ul>
               <div style={{ marginTop: '16px' }}>
                 <img 
                     src="/Flat_illutrations/diverse_group_in_garden.png" 
                     alt="Team Collaboration" 
                     style={{ width: '100%', borderRadius: '4px' }} 
                     onError={(e) => e.target.style.display = 'none'}
                 />
               </div>
           </div>
           <div className="li-footer-links">
                <Link to="/about">{t('footer.about')}</Link>
                <Link to="/accessibility">{t('footer.accessibility')}</Link>
                <Link to="/help-center">{t('footer.helpCenter')}</Link>
                <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
                <span>{t('auth.footer.rights')}</span>
           </div>
        </aside>

      </div>
    </div>
  );
}

export default InviteEmployees;